import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useLanguage } from "./LanguageContext";

// Professional icon component with elegant styling
const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 hover:text-blue-700 transition-colors">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

export function Deals() {
  const { getBilingualText } = useLanguage();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">{getBilingualText("deals.header.title")}</h2>
        <p className="text-muted-foreground">{getBilingualText("deals.subtitle")}</p>
      </div>

      {/* Deal Card - Bellamy Essencial Rooms */}
      <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left Side - Image and Location */}
            <div className="p-6 space-y-4">
              {/* Property Image */}
              <div className="relative">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZ3xlbnwxfHx8fDE3NTY0OTU4MDB8MA&ixlib=rb-4.1.0&q=80&w=800&utm_source=figma&utm_medium=referral"
                  alt="Bellamy Essencial Rooms - Edificio en Málaga Centro"
                  className="w-full h-64 object-cover rounded-lg shadow-sm"
                />
                <div className="absolute top-4 left-4 space-y-2">
                  <Badge className="bg-black/80 text-white backdrop-blur-sm">
                    BELLAMY ESSENCIAL ROOMS
                  </Badge>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-3 bg-gray-50 p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase tracking-wide">{getBilingualText("deals.metrics.units")}</div>
                  <div className="font-bold text-lg">17 + 2*</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase tracking-wide">{getBilingualText("deals.metrics.builtArea")}</div>
                  <div className="font-bold text-lg">1.547m2</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase tracking-wide">{getBilingualText("deals.metrics.roomPotential")}</div>
                  <div className="font-bold text-lg">66</div>
                </div>
              </div>

              {/* Pro-rata Information */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Tu pro-rata disponible:</span>
                    <span className="text-lg font-bold text-blue-600">€55.000</span>
                  </div>
                  <div className="border-t border-blue-200 pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Fecha límite inversión:</span>
                      <span className="font-medium text-gray-900">15 Feb 2025</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Compra prevista:</span>
                      <span className="font-medium text-gray-900">28 Feb 2025</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                *{getBilingualText("deals.commercial.locals")}
              </div>
            </div>

            {/* Right Side - Details */}
            <div className="p-6 space-y-6 bg-gray-50">
              {/* General Information */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-900">{getBilingualText("deals.sections.general")}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.name")}</span>
                    <span className="text-sm font-medium text-blue-600">Bellamy La Unión</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.address")}</span>
                    <span className="text-sm font-medium">Pje. Emilio López Cerezo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.city")}</span>
                    <span className="text-sm font-medium">Málaga Centro</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.catastralRef")}</span>
                    <span className="text-sm font-medium text-blue-600">1739201UF7613N0001PT</span>
                  </div>
                </div>
              </div>

              {/* Strategy */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-900">{getBilingualText("deals.sections.strategy")}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.strategy")}</span>
                    <span className="text-sm font-medium">{getBilingualText("deals.strategy.description")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{getBilingualText("deals.fields.typology")}</span>
                    <span className="text-sm font-medium text-blue-600">{getBilingualText("deals.typology.residential")}</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-900">{getBilingualText("deals.sections.timing")}</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{getBilingualText("deals.timeline.negotiations")}</div>
                      <div className="text-xs text-gray-600">may-2025</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{getBilingualText("deals.timeline.contract")}</div>
                      <div className="text-xs text-gray-600">jul-2025</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{getBilingualText("deals.timeline.purchase")}</div>
                      <div className="text-xs text-gray-600">oct-2025</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1">
                  {getBilingualText("deals.actions.viewDetails")}
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {getBilingualText("deals.actions.investNow")}
                </Button>
              </div>

              {/* Location Link */}
              <div className="pt-2">
                <a 
                  href="#" 
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <MapPinIcon />
                  {getBilingualText("deals.actions.viewMaps")}
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}